import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import { APIResponse } from './types/api-response.type';
import { FamilyMember, User } from './types/auth.types';
import { UserAuthService } from './user-auth.service';

@Injectable({
  providedIn: 'root',
})

/**
 * Simple service used to track the family member concerned by the flow form
 * We put it in a separate service to have it usable in multiple flows
 */
export class FamilyMemberFormService {
  private _familyMemberId: string;

  get familyMemberId(): string {
    return this._familyMemberId;
  }

  set familyMemberId(value: string) {
    this._familyMemberId = value;
  }

  mainMemberIdSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  userInfo: User;

  private _mainMemberId: string;

  get mainMemberId(): string {
    return this._mainMemberId;
  }

  set mainMemberId(value: string) {
    this._mainMemberId = value;
  }

  private _familyMember: FamilyMember;

  get familyMember(): FamilyMember {
    return this._familyMember;
  }

  set familyMember(value: FamilyMember) {
    this._familyMember = value;
    this._familyMemberId = value?.familyMemberId;
  }

  constructor(private authService: UserAuthService) {
    this.fetchMainMemberId(true);
  }

  fetchMainMemberId(initial = false) {
    if (!this.mainMemberId) {
      if (this.authService.isUserLoggedIn()) {
        this.authService
          .getuserByToken({ token: this.authService.getCurrentUser().token })
          .subscribe((res: APIResponse<User>) => {
            if (res.status === 'SUCCESS' && res.data) {
              this.userInfo = res.data;
              this.mainMemberId = res.data.familyMemberId;
              this.mainMemberIdSubject.next(this.mainMemberId);
              if (initial || !this.familyMemberId) {
                this.familyMemberId = this.mainMemberId;
              }
            }
          });
      }
    }
  }

  fetchMainUserInfo() {
    const obs = new BehaviorSubject<User>(null);
    if (this.authService.isUserLoggedIn()) {
      this.authService.getuserByToken({ token: this.authService.getCurrentUser().token }).subscribe(
        (res: APIResponse<User>) => {
          if (res.status === 'SUCCESS' && res.data) {
            this.userInfo = res.data;
            this.mainMemberId = res.data.familyMemberId;
            this.mainMemberIdSubject.next(this.mainMemberId);
            obs.next(res.data);
            obs.complete();
          } else {
            obs.next(null);
            obs.complete();
          }
        },
        () => {
          obs.next(null);
          obs.complete();
        },
      );
    } else {
      obs.next(null);
      obs.complete();
    }
    return obs;
  }

  setCurrentFamilyMember(familyMemberId: string) {
    const response = new Subject<FamilyMember>();
    this.familyMemberId = familyMemberId;
    this.authService
      .getFamilymember({ familyMemberId })
      .subscribe((res: APIResponse<FamilyMember>) => {
        if (res.status === 'SUCCESS') {
          this.familyMember = res.data;
          response.next(res.data);
        }
      });
    return response;
  }

  reset() {
    this.familyMember = null;
    this.userInfo = null;
    this.mainMemberId = null;
  }
}
